import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthentificationService } from '@core/services/authentification/authentification.service';
import { UserService } from '@core/services/user/user.service';
import { environment } from '@environments/environment';
import { PrinterAppUrl } from '@shared/model/virtual-printer-models/virtual-printer-new.model';
import { catchError, firstValueFrom, lastValueFrom, Observable, throwError } from 'rxjs';
import { GROUP_USERNAME_CONTEXT, USER_GROUP_CONTEXT, USER_NAME_CONTEXT } from './request.context';
import { NotificationService } from '@shared/services/notification.service';

export const authRequestInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const ignoredPatterns = [
    environment.sso_url,
    'amazonaws.com/dev-submit-by-generix-email.mypbconnect.com/INPUT_EMAIL/',
    PrinterAppUrl,
    'X-Amz-Security-Token=',
    './assets/',
    'oauth2/token',
    'oauth2/authorize',
  ];

  const userService = inject(UserService);
  const authentificationService = inject(AuthentificationService);
  const notificationService = inject(NotificationService);
  const tokenErrorStatus = [401, 403];

  if (ignoredPatterns.some((pattern) => req.url.includes(pattern))) {
    return next(req);
  }

  let clonedRequest = req;

  const contextBody = {
    group: userService.userGroup(),
    user: userService.userName(),
    user_name: userService.userName(),
  };

  if (!req.context.get(GROUP_USERNAME_CONTEXT) || !req.context.get(USER_NAME_CONTEXT)) {
    delete contextBody.user;
    delete contextBody.user_name;
  }

  if (!req.context.get(GROUP_USERNAME_CONTEXT) || !req.context.get(USER_GROUP_CONTEXT)) {
    delete contextBody.group;
  }

  if (Object.keys(contextBody).length && !['GET', 'DELETE'].includes(clonedRequest.method)) {
    const originalBody =
      clonedRequest.body && typeof clonedRequest.body === 'object' && !(clonedRequest.body instanceof FormData)
        ? (clonedRequest.body as { [key: string]: any })
        : {};

    const bodyWithContext = {
      ...originalBody,
      ...(originalBody.user === undefined && originalBody.user_name === undefined
        ? { user: contextBody.user, user_name: contextBody.user_name }
        : {}),
      ...(originalBody.group === undefined ? { group: contextBody.group } : {}),
    };

    clonedRequest = clonedRequest.clone({ body: bodyWithContext });
  }

  const addAuthToken = (request: HttpRequest<any>) => {
    const token = authentificationService.authUser()?.accessToken;
    const cloned = request.clone({ url: buildUrl(request.url) });
    if (!token) {
      return cloned;
    }
    return cloned.clone({ headers: cloned.headers.set('Authorization', token) });
  };

  const refreshToken = async (request: HttpRequest<unknown>) => {
    await firstValueFrom(authentificationService.refreshToken());

    return await lastValueFrom(
      next(addAuthToken(request)).pipe(
        catchError((error: any) => {
          if (tokenErrorStatus.includes(error.status)) {
            if (error.status === 401) {
              authentificationService.logout();
            } else {
              notificationService.error('error.forbidden');
            }
          }
          return throwError(() => error);
        }),
      ),
    );
  };

  const buildUrl = (url: string): string => {
    if (!['https://', 'http://'].some((proto) => url.startsWith(proto))) {
      return `${environment.api_url}${url}`;
    }
    return url;
  };

  return next(addAuthToken(clonedRequest)).pipe(
    catchError((error) => {
      if (tokenErrorStatus.includes(error.status)) {
        return refreshToken(clonedRequest);
      }
      return throwError(() => error);
    }),
  );
};
